import { AxiosResponse } from 'axios';
import { ApiProjects } from 'src/shared/api/ApiProject';
import { PROJECTS_URL } from 'src/shared/config';

import TokenService from '../token/TokenService';

import {
  BatchRowsData,
  BatchRowsParams,
  BatchRowsRes,
  BatchSubsetApplyResponse,
  BatchSubsetOperationRequest,
  BatchSubsetPreviewResponse,
  CellComment,
  CellHistoryItem,
  ColumnAccessPermissionsResponse,
  CreateCellCommentPayload,
  FormAccessPermissionsResponse,
  MassOperationAuditItem,
  MassOperationAuditQueryParams,
  RecalculateRulesParams,
  ReplaceColumnAccessPermissionsPayload,
  ReplaceFormAccessPermissionsPayload,
  RulesRecalculationRes,
  TableQueryParams,
  UpdateCellCommentPayload,
} from './types';

const batchRows = (params: BatchRowsParams) =>
  ApiProjects.post<string, Promise<AxiosResponse<BatchRowsRes>>, BatchRowsData>(
    `/table/${params.table_id}/batch`,
    params.data,
  );

const batchSubsetPreview = (tableId: number, payload: BatchSubsetOperationRequest) =>
  ApiProjects.post<
    string,
    Promise<AxiosResponse<BatchSubsetPreviewResponse>>,
    BatchSubsetOperationRequest
  >(`/table/${tableId}/batch/subset/preview`, payload);

const batchSubsetApply = (tableId: number, payload: BatchSubsetOperationRequest) =>
  ApiProjects.post<
    string,
    Promise<AxiosResponse<BatchSubsetApplyResponse>>,
    BatchSubsetOperationRequest
  >(`/table/${tableId}/batch/subset/apply`, payload);

const recalculateTableRules = (tableId: number, params?: RecalculateRulesParams) => {
  const searchParams = new URLSearchParams();
  if (params?.includeDependent) {
    searchParams.set('include_dependent', 'true');
  }
  if (params?.rowIds?.length) {
    const rowIDs = Array.from(
      new Set(
        params.rowIds
          .map(id => Number(id))
          .filter(id => Number.isFinite(id) && id > 0)
          .map(id => Math.floor(id)),
      ),
    );
    if (rowIDs.length) {
      searchParams.set('row_ids', rowIDs.join(','));
    }
  }

  const query = searchParams.toString();
  return ApiProjects.post<string, Promise<AxiosResponse<RulesRecalculationRes>>>(
    `/table/${tableId}/rules/recalculate${query ? `?${query}` : ''}`,
  );
};

const recalculateAllRules = () =>
  ApiProjects.post<string, Promise<AxiosResponse<RulesRecalculationRes>>>(`/rules/recalculate`);

const importExcel = (tableId: number, params: FormData) =>
  ApiProjects.post<string, string, FormData>(`/table/${tableId}/parse`, params, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

const exportExcel = (tableId: number) => {
  const url = `${PROJECTS_URL}table/${tableId}/export`;
  const accessToken = TokenService.getAccessToken();

  return fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  }).then(response => {
    if (!response.ok) {
      throw new Error(`Failed to export: ${response.status}`);
    }

    return response.blob();
  });
};

const buildQuery = (params?: TableQueryParams) => {
  const searchParams = new URLSearchParams();

  if (typeof params?.row_id === 'number') {
    searchParams.set('row_id', String(params.row_id));
  }
  if (params?.column_name) {
    searchParams.set('column_name', params.column_name);
  }
  if (typeof params?.limit === 'number') {
    searchParams.set('limit', String(params.limit));
  }
  if (typeof params?.offset === 'number') {
    searchParams.set('offset', String(params.offset));
  }

  const query = searchParams.toString();
  return query ? `?${query}` : '';
};

const buildMassAuditQuery = (params?: MassOperationAuditQueryParams) => {
  const searchParams = new URLSearchParams();

  if (params?.operation) {
    searchParams.set('operation', params.operation);
  }
  if (typeof params?.limit === 'number') {
    searchParams.set('limit', String(params.limit));
  }
  if (typeof params?.offset === 'number') {
    searchParams.set('offset', String(params.offset));
  }

  const query = searchParams.toString();
  return query ? `?${query}` : '';
};

const getCellComments = (tableId: number, params?: TableQueryParams) =>
  ApiProjects.get<void, Promise<AxiosResponse<CellComment[]>>>(
    `/table/${tableId}/comments${buildQuery(params)}`,
  );

const createCellComment = (tableId: number, payload: CreateCellCommentPayload) =>
  ApiProjects.post<string, Promise<AxiosResponse<CellComment>>, CreateCellCommentPayload>(
    `/table/${tableId}/comments`,
    payload,
  );

const updateCellComment = (tableId: number, commentId: number, payload: UpdateCellCommentPayload) =>
  ApiProjects.put<string, Promise<AxiosResponse<{ updated: boolean }>>, UpdateCellCommentPayload>(
    `/table/${tableId}/comments/${commentId}`,
    payload,
  );

const deleteCellComment = (tableId: number, commentId: number) =>
  ApiProjects.delete<void, Promise<AxiosResponse<void>>>(`/table/${tableId}/comments/${commentId}`);

const getCellHistory = (tableId: number, params?: TableQueryParams) =>
  ApiProjects.get<void, Promise<AxiosResponse<CellHistoryItem[]>>>(
    `/table/${tableId}/history${buildQuery(params)}`,
  );

const getMassOperationAudit = (tableId: number, params?: MassOperationAuditQueryParams) =>
  ApiProjects.get<void, Promise<AxiosResponse<MassOperationAuditItem[]>>>(
    `/table/${tableId}/audit${buildMassAuditQuery(params)}`,
  );

const getFormAccessPermissions = (tableId: number) =>
  ApiProjects.get<void, Promise<AxiosResponse<FormAccessPermissionsResponse>>>(
    `/table/${tableId}/access`,
  );

const replaceFormAccessPermissions = (
  tableId: number,
  payload: ReplaceFormAccessPermissionsPayload,
) =>
  ApiProjects.put<
    string,
    Promise<AxiosResponse<FormAccessPermissionsResponse>>,
    ReplaceFormAccessPermissionsPayload
  >(`/table/${tableId}/access`, payload);

const getColumnAccessPermissions = (tableId: number) =>
  ApiProjects.get<void, Promise<AxiosResponse<ColumnAccessPermissionsResponse>>>(
    `/table/${tableId}/access/columns`,
  );

const replaceColumnAccessPermissions = (
  tableId: number,
  payload: ReplaceColumnAccessPermissionsPayload,
) =>
  ApiProjects.put<
    string,
    Promise<AxiosResponse<ColumnAccessPermissionsResponse>>,
    ReplaceColumnAccessPermissionsPayload
  >(`/table/${tableId}/access/columns`, payload);

const TableService = {
  batchRows,
  batchSubsetPreview,
  batchSubsetApply,
  recalculateTableRules,
  recalculateAllRules,
  importExcel,
  exportExcel,
  getCellComments,
  createCellComment,
  updateCellComment,
  deleteCellComment,
  getCellHistory,
  getMassOperationAudit,
  getFormAccessPermissions,
  replaceFormAccessPermissions,
  getColumnAccessPermissions,
  replaceColumnAccessPermissions,
};

export default TableService;
